/**
 * Rules-as-data: the canonical types + validator for security rules (IP/access/rate/
 * virtual-patch/signature). See docs/specs/rules-as-data-schema.md.
 *
 * HARD CONSTRAINT (ADR-0003 / WP.org guideline #8): a rule is pure declarative data
 * evaluated by a CLOSED, fixed vocabulary of fields + operators that the engines already
 * ship. There is no expression language and nothing here ever becomes executable code.
 * Adding a field/operator is a code release, never a pushed rule.
 */

import { parseIpv4Cidr } from './cidr.js';
import { compileLinearRegex } from './regex-linear.js';
import { assessRegex } from './regex-safety.js';

export const RULES_SCHEMA_VERSION = 1 as const;

export type RuleType = 'ip' | 'access' | 'rate' | 'vpatch' | 'signature';
export type RuleScope = 'global' | 'tenant' | 'site';
export type RulePlane = 'edge' | 'wp';
export type RuleSource = 'manual' | 'fleet_intel' | 'vuln_catalog' | 'managed_ruleset';

/** Request attributes a condition may reference. `query.*`/`body.*`/`header.*` take a suffix. */
export const MATCH_FIELDS_EXACT = [
  'method',
  'path',
  'ip',
  'asn',
  'geo',
  'authenticated',
  'role',
  'package.version',
] as const;
export const MATCH_FIELD_PREFIXES = ['query.', 'body.', 'header.'] as const;

/** The only operators the engines implement. */
export const MATCH_OPERATORS = [
  'equals',
  'not_equals',
  'in',
  'cidr',
  'regex',
  'glob',
  'gt',
  'lt',
  'gte',
  'lte',
  'exists',
  'contains',
  'version_in_range',
] as const;
export type MatchOperator = (typeof MATCH_OPERATORS)[number];

export interface Condition {
  field: string;
  op: MatchOperator;
  value?: unknown;
}

export type MatchNode =
  | { all: MatchNode[] }
  | { any: MatchNode[] }
  | { not: MatchNode }
  | Condition;

export type RuleAction =
  | { kind: 'block'; status?: number }
  | { kind: 'challenge'; method?: 'turnstile' | 'js' }
  /**
   * `limit` is the budget for one identity — (source, account) — and `ipLimit` the ceiling for a
   * single source across all accounts it tries. A request is limited when EITHER is exceeded.
   *
   * Two counters, because either alone is defeatable. Per-source alone charges every visitor behind
   * one NAT to the same budget. Per-identity alone is worse: the account name comes from the request
   * body, so an attacker varies it (`log=a1`, `log=a2`, …), lands in a fresh counter every time, and
   * is never limited — which is exactly the password-spraying shape.
   *
   * `ipLimit` is optional and defaults to `limit`, i.e. omitting it reproduces per-source-only
   * behaviour exactly. Raising it is how an operator buys room for a shared egress address, and it
   * is a per-rule data change rather than a deploy.
   */
  | {
      kind: 'rate_limit';
      limit: number;
      windowSec: number;
      ipLimit?: number;
      then?: 'challenge' | 'block';
    }
  | { kind: 'log' }
  | { kind: 'allow' };

export interface RuleMeta {
  source: RuleSource;
  cve?: string;
  confidence?: number; // 0..1
  description?: string;
  createdAt?: string;
  expiresAt?: string;
}

export interface Rule {
  schemaVersion: number;
  id: string;
  version: number;
  type: RuleType;
  enabled: boolean;
  scope: RuleScope;
  scopeId?: string | null;
  planes: RulePlane[];
  priority: number;
  action: RuleAction;
  match: MatchNode;
  meta: RuleMeta;
}

/** Safety bounds enforced at ingest (and assumed by the engines). */
export const RULE_LIMITS = {
  maxDepth: 6,
  maxConditions: 40,
  maxRegexLength: 200,
  maxArrayValues: 1024,
} as const;

const RULE_TYPES = new Set<RuleType>(['ip', 'access', 'rate', 'vpatch', 'signature']);
const RULE_SCOPES = new Set<RuleScope>(['global', 'tenant', 'site']);
const RULE_PLANES = new Set<RulePlane>(['edge', 'wp']);
const RULE_SOURCES = new Set<RuleSource>([
  'manual',
  'fleet_intel',
  'vuln_catalog',
  'managed_ruleset',
]);
const OPERATORS = new Set<string>(MATCH_OPERATORS);
const FIELDS_EXACT = new Set<string>(MATCH_FIELDS_EXACT);

export type ValidationResult = { ok: true; rule: Rule } | { ok: false; errors: string[] };

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function isAllowedField(field: string): boolean {
  if (FIELDS_EXACT.has(field)) return true;
  return MATCH_FIELD_PREFIXES.some((p) => field.startsWith(p) && field.length > p.length);
}

/** True if a rule has an `expiresAt` in the past (a parseable timestamp ≤ now). */
export function isRuleExpired(rule: Rule, now: number): boolean {
  const exp = rule.meta.expiresAt;
  if (!exp) return false;
  const t = Date.parse(exp);
  return !Number.isNaN(t) && t <= now;
}

/**
 * Select the rules a given plane should be *served* (spec §5 step 1, §7): enabled, not expired,
 * and enforceable on that plane. Pure — the same predicate the engine applies at evaluation, so
 * what a plane receives matches what it would enforce. Used by the WP sync + edge distribution
 * paths to shape payloads by each rule's `planes` routing and honor the TTL kill switch.
 */
export function selectEnforceable(
  rules: readonly Rule[],
  opts: { plane: RulePlane; now?: number },
): Rule[] {
  const now = opts.now ?? Date.now();
  return rules.filter((r) => r.enabled && r.planes.includes(opts.plane) && !isRuleExpired(r, now));
}

function validateAction(a: unknown, errors: string[]): void {
  if (!isObject(a) || typeof a.kind !== 'string') {
    errors.push('action: must be an object with a "kind"');
    return;
  }
  switch (a.kind) {
    case 'block':
      /*
       * A refusal status, not any number. Two distinct failures were reachable from "number":
       *
       *  - **Outside 200–599 the edge takes the site down.** `new Response(body, { status })` throws
       *    a RangeError per the fetch spec, `applyDecision` is not individually guarded, and the
       *    Worker's outer catch answers 502 — so one rule authored `status: 999` returns a gateway
       *    error on every request it matches, indefinitely. An availability bug reachable from the
       *    rule form.
       *  - **A 2xx block is a cacheable page.** A CDN happily stores a 200 and replays the refusal
       *    body to visitors who were never blocked. A block must be uncacheable by construction,
       *    not by remembering to set headers.
       *
       * 3xx is excluded too: a redirect with no `Location` is undefined behaviour dressed as a
       * decision. The connector's PHP engine clamps to the same window, so both planes refuse
       * identically rather than one clamping and the other throwing.
       */
      if (
        a.status !== undefined &&
        (typeof a.status !== 'number' ||
          !Number.isInteger(a.status) ||
          a.status < 400 ||
          a.status > 599)
      ) {
        errors.push('action.status: integer 400–599');
      }
      break;
    case 'challenge':
      if (a.method !== undefined && a.method !== 'turnstile' && a.method !== 'js')
        errors.push('action.method: turnstile|js');
      break;
    case 'rate_limit':
      if (typeof a.limit !== 'number' || typeof a.windowSec !== 'number')
        errors.push('action.rate_limit: limit and windowSec must be numbers');
      // Optional and additive: rules written before `ipLimit` existed stay valid and behave as they
      // always did. A present-but-nonsense value is rejected rather than coerced — silently reading
      // `ipLimit: 0` as "no limit" or as "limit everything" are both wrong, and one of them is an
      // outage.
      if (a.ipLimit !== undefined && (typeof a.ipLimit !== 'number' || a.ipLimit <= 0))
        errors.push('action.ipLimit: must be a positive number when present');
      if (a.then !== undefined && a.then !== 'challenge' && a.then !== 'block')
        errors.push('action.then: challenge|block');
      break;
    case 'log':
    case 'allow':
      break;
    default:
      errors.push(`action.kind: unknown "${String(a.kind)}"`);
  }
}

function validateCondition(c: Record<string, unknown>, errors: string[]): void {
  if (typeof c.field !== 'string' || !isAllowedField(c.field)) {
    errors.push(`condition.field: not in closed vocabulary ("${String(c.field)}")`);
  }
  if (typeof c.op !== 'string' || !OPERATORS.has(c.op)) {
    errors.push(`condition.op: unknown operator ("${String(c.op)}")`);
    return;
  }
  const op = c.op as MatchOperator;
  const v = c.value;
  switch (op) {
    case 'in':
      if (!Array.isArray(v)) errors.push(`condition(${op}): value must be an array`);
      else if (v.length > RULE_LIMITS.maxArrayValues)
        errors.push(`condition(${op}): array too large`);
      break;
    case 'cidr':
      if (!Array.isArray(v)) errors.push('condition(cidr): value must be an array');
      else if (v.length > RULE_LIMITS.maxArrayValues)
        errors.push('condition(cidr): array too large');
      else {
        for (const [index, cidr] of v.entries()) {
          if (typeof cidr !== 'string' || parseIpv4Cidr(cidr) === null) {
            errors.push(
              `condition(cidr): value[${index}] must be an IPv4 CIDR or bare IPv4 address`,
            );
          }
        }
      }
      break;
    case 'regex':
      if (typeof v !== 'string') errors.push('condition(regex): value must be a string');
      else if (v.length > RULE_LIMITS.maxRegexLength)
        errors.push('condition(regex): pattern too long');
      else {
        /*
         * Refuse catastrophic backtracking at authoring time (#34).
         *
         * The length cap does nothing here — `^(a+)+$` is nine characters. And nothing downstream
         * can rescue us: JavaScript cannot abort a running regex, so the edge Worker's fail-open
         * catch never runs; Cloudflare kills the isolate for exceeding CPU and the visitor gets a
         * gateway error. This is the one path that breaks golden rule 5 without any code deciding
         * to, which is why the gate belongs here, before such a rule can exist.
         */
        const safety = assessRegex(v);
        if (!safety.safe) errors.push(`condition(regex): ${safety.reason}`);
        /*
         * And refuse anything the linear matcher cannot compile (#34).
         *
         * The engine matches on a Thompson NFA, which has no fallback to `RegExp` — a pattern it
         * cannot compile simply never matches. Caught here, that is a rule the operator is told is
         * invalid; uncaught, it is a rule that exists, looks enforced in the console, and silently
         * protects nothing. The second is the failure this codebase keeps rediscovering, so the
         * authoring gate and the matcher have to agree on what is expressible.
         */
        const compiled = compileLinearRegex(v);
        if (!compiled.ok) errors.push(`condition(regex): ${compiled.reason}`);
      }
      break;
    case 'gt':
    case 'lt':
    case 'gte':
    case 'lte':
      if (typeof v !== 'number') errors.push(`condition(${op}): value must be a number`);
      break;
    case 'exists':
      break; // value optional
    case 'equals':
    case 'not_equals':
    case 'glob':
    case 'contains':
    case 'version_in_range':
      if (typeof v !== 'string' && typeof v !== 'number' && typeof v !== 'boolean')
        errors.push(`condition(${op}): value must be a string, number, or boolean`);
      break;
  }
}

function validateMatch(
  node: unknown,
  depth: number,
  state: { count: number },
  errors: string[],
): void {
  if (depth > RULE_LIMITS.maxDepth) {
    errors.push(`match: nesting exceeds maxDepth (${RULE_LIMITS.maxDepth})`);
    return;
  }
  if (!isObject(node)) {
    errors.push('match: node must be an object');
    return;
  }
  if ('all' in node || 'any' in node) {
    const arr = (node as { all?: unknown; any?: unknown }).all ?? (node as { any?: unknown }).any;
    if (!Array.isArray(arr) || arr.length === 0) {
      errors.push('match: all/any must be a non-empty array');
      return;
    }
    for (const child of arr) validateMatch(child, depth + 1, state, errors);
    return;
  }
  if ('not' in node) {
    validateMatch((node as { not: unknown }).not, depth + 1, state, errors);
    return;
  }
  // Leaf condition.
  state.count += 1;
  if (state.count > RULE_LIMITS.maxConditions) {
    errors.push(`match: exceeds maxConditions (${RULE_LIMITS.maxConditions})`);
    return;
  }
  validateCondition(node, errors);
}

/**
 * Validate an untrusted rule against the schema + closed vocabulary + safety bounds.
 * Used at platform ingest before a rule can enter the store or be distributed.
 */
export function validateRule(input: unknown): ValidationResult {
  const errors: string[] = [];
  if (!isObject(input)) return { ok: false, errors: ['rule: must be an object'] };

  if (input.schemaVersion !== RULES_SCHEMA_VERSION)
    errors.push(`schemaVersion: unsupported (expected ${RULES_SCHEMA_VERSION})`);
  if (typeof input.id !== 'string' || input.id.length === 0) errors.push('id: required string');
  if (typeof input.version !== 'number') errors.push('version: required number');
  if (typeof input.enabled !== 'boolean') errors.push('enabled: required boolean');
  if (typeof input.priority !== 'number') errors.push('priority: required number');

  if (typeof input.type !== 'string' || !RULE_TYPES.has(input.type as RuleType))
    errors.push('type: must be ip|access|rate|vpatch|signature');

  if (typeof input.scope !== 'string' || !RULE_SCOPES.has(input.scope as RuleScope)) {
    errors.push('scope: must be global|tenant|site');
  } else if (input.scope !== 'global' && !input.scopeId) {
    errors.push('scopeId: required when scope is tenant|site');
  }

  if (
    !Array.isArray(input.planes) ||
    input.planes.length === 0 ||
    !input.planes.every((p) => RULE_PLANES.has(p as RulePlane))
  ) {
    errors.push('planes: non-empty array of edge|wp');
  }

  validateAction(input.action, errors);

  if (input.match === undefined) errors.push('match: required');
  else validateMatch(input.match, 1, { count: 0 }, errors);

  if (!isObject(input.meta) || !RULE_SOURCES.has(input.meta.source as RuleSource)) {
    errors.push('meta.source: must be manual|fleet_intel|vuln_catalog|managed_ruleset');
  } else if (
    input.meta.confidence !== undefined &&
    (typeof input.meta.confidence !== 'number' ||
      input.meta.confidence < 0 ||
      input.meta.confidence > 1)
  ) {
    errors.push('meta.confidence: must be a number in [0,1]');
  }

  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, rule: input as unknown as Rule };
}
